import type { DocumentData } from "firebase/firestore";
import { dateValue, stringValue, trimmedNonEmpty } from "./firestore";
import { sbotSlug, type LeagueCompetitionUniversalFieldRole } from "./fieldDefinition";
import type { LeagueMemberSummary } from "./memberSummary";

// Ports of GPSTrackerModel/GPSMapModel data types. Firestore contracts per
// firestore.rules validGPSTrackerDocumentShape / SampleShape / HistoryShape /
// validGPSAssignmentState.

export const DEFAULT_SAMPLE_INTERVAL_SECONDS = 30;

// The KNOWN categories (mirrors GPSAthleteCategory in Models/GPSMapModel.swift,
// which iOS still writes exclusively). Since 2026-07 the field itself is
// free-form (1-40 chars, see validGPSAthleteCategory in firestore.rules) so
// events can track custom groups — e.g. a non-binary category — end to end.
export type GPSAthleteCategory = "MPRO" | "FPRO" | "M-AGE-GROUP" | "F-AGE-GROUP" | "STAFF";

export const ATHLETE_CATEGORIES: GPSAthleteCategory[] = ["MPRO", "FPRO", "M-AGE-GROUP", "F-AGE-GROUP", "STAFF"];

export const ATHLETE_CATEGORY_LABELS: Record<GPSAthleteCategory, string> = {
  MPRO: "Men's Pro",
  FPRO: "Women's Pro",
  "M-AGE-GROUP": "Men's Age Group",
  "F-AGE-GROUP": "Women's Age Group",
  STAFF: "Staff",
};

export const ATHLETE_CATEGORY_MAX_LENGTH = 40;

/** Free-form category values: trimmed, 1-40 chars; undefined otherwise. */
export function parseAthleteCategory(value: unknown): string | undefined {
  const trimmed = trimmedNonEmpty(stringValue(value));
  return trimmed ? trimmed.slice(0, ATHLETE_CATEGORY_MAX_LENGTH) : undefined;
}

/** Friendly label for known categories, the raw identifier for custom ones. */
export function athleteCategoryLabel(category: string): string {
  return (ATHLETE_CATEGORY_LABELS as Record<string, string>)[category] ?? category;
}

export type CourseKMLRole = "swimKML" | "bikeKML" | "runKML" | "courseKML";
export const COURSE_KML_ROLES: CourseKMLRole[] = ["swimKML", "bikeKML", "runKML", "courseKML"];

export interface GPSTrackerCourseKML {
  /** Rules require id == role. */
  id: CourseKMLRole;
  fieldID: string;
  role: CourseKMLRole;
  name: string;
  url: string;
}

export function isCourseKMLRole(role: LeagueCompetitionUniversalFieldRole | string): role is CourseKMLRole {
  return (COURSE_KML_ROLES as string[]).includes(role);
}

export function parseCourseKML(data: unknown): GPSTrackerCourseKML | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const role = record.role;
  const fieldID = stringValue(record.fieldID);
  const url = stringValue(record.url);
  if (!isCourseKMLRole(String(role)) || !fieldID || !url) return null;
  const typedRole = role as CourseKMLRole;
  return {
    id: typedRole,
    fieldID,
    role: typedRole,
    name: stringValue(record.name) ?? typedRole,
    url,
  };
}

export function courseKMLFirestoreData(kml: GPSTrackerCourseKML): Record<string, unknown> {
  return { id: kml.id, fieldID: kml.fieldID, role: kml.role, name: kml.name, url: kml.url };
}

export interface GPSTrackerSample {
  latitude: number;
  longitude: number;
  /** Unix seconds; rules require an integer. */
  gpstime: number;
  speedUS_mph: number;
  altitudeUS_feet: number;
  speedMetric_kmh: number;
  altitudeMetric_meters: number;
  lte_conditions: string;
  cellularstrength: string | null;
  battery: number | null;
  heading?: number | null;
}

export function parseSample(data: unknown): GPSTrackerSample | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const latitude = record.latitude;
  const longitude = record.longitude;
  const gpstime = record.gpstime;
  if (typeof latitude !== "number" || typeof longitude !== "number" || typeof gpstime !== "number") {
    return null;
  }
  return {
    latitude,
    longitude,
    gpstime,
    speedUS_mph: typeof record.speedUS_mph === "number" ? record.speedUS_mph : 0,
    altitudeUS_feet: typeof record.altitudeUS_feet === "number" ? record.altitudeUS_feet : 0,
    speedMetric_kmh: typeof record.speedMetric_kmh === "number" ? record.speedMetric_kmh : 0,
    altitudeMetric_meters: typeof record.altitudeMetric_meters === "number" ? record.altitudeMetric_meters : 0,
    lte_conditions: stringValue(record.lte_conditions) ?? "",
    cellularstrength: stringValue(record.cellularstrength) ?? null,
    battery: typeof record.battery === "number" ? record.battery : null,
    heading: typeof record.heading === "number" ? record.heading : null,
  };
}

export interface GPSTrackerDocument {
  id: string;
  userID: string;
  leagueID?: string;
  competitionInstanceID: string;
  displayName?: string;
  gpsAlias?: string;
  gpsID?: string;
  /** Known GPSAthleteCategory value or a free-form custom tracking identifier. */
  athleteCat?: string;
  deviceName?: string;
  sampleIntervalSeconds: number;
  isTransmitting: boolean;
  startedAt?: Date;
  lastUpdatedAt?: Date;
  eventTimeZoneIdentifier?: string;
  gpsWindowStart?: Date;
  gpsWindowEnd?: Date;
  selectedCourseKML?: GPSTrackerCourseKML;
  latestSample?: GPSTrackerSample;
  /** The device's fix before latestSample — gives the matcher a travel bearing. */
  previousSample?: GPSTrackerSample;
}

export function gpsDataDocumentID(userID: string, instanceID: string): string {
  return `gpsData_${userID}_${instanceID}`;
}

export function parseTrackerDocument(id: string, data: DocumentData): GPSTrackerDocument | null {
  const userID = stringValue(data.userID);
  const competitionInstanceID = stringValue(data.competitionInstanceID);
  if (!userID || !competitionInstanceID || typeof data.isTransmitting !== "boolean") {
    return null;
  }
  const locations = Array.isArray(data.locations) ? data.locations : [];
  const samples = locations
    .map(parseSample)
    .filter((sample): sample is GPSTrackerSample => sample !== null);
  const latestSample = samples[0];
  // Newest-first; skip duplicate timestamps so the pair spans real movement.
  const previousSample = samples.find((sample) => latestSample && sample.gpstime < latestSample.gpstime);
  return {
    id,
    userID,
    leagueID: stringValue(data.leagueID),
    competitionInstanceID,
    displayName: stringValue(data["display-name"]),
    gpsAlias: stringValue(data["gps-alias"]),
    gpsID: stringValue(data["gps-id"]),
    athleteCat: parseAthleteCategory(data["athlete-cat"]),
    deviceName: stringValue(data.deviceName),
    sampleIntervalSeconds: Math.max(
      1,
      typeof data.sampleIntervalSeconds === "number" ? data.sampleIntervalSeconds : DEFAULT_SAMPLE_INTERVAL_SECONDS,
    ),
    isTransmitting: data.isTransmitting,
    startedAt: dateValue(data.startedAt),
    lastUpdatedAt: dateValue(data.lastUpdatedAt),
    eventTimeZoneIdentifier: stringValue(data.eventTimeZoneIdentifier),
    gpsWindowStart: dateValue(data.gpsWindowStart),
    gpsWindowEnd: dateValue(data.gpsWindowEnd),
    selectedCourseKML: parseCourseKML(data.selectedCourseKML) ?? undefined,
    latestSample,
    previousSample,
  };
}

export interface GPSAssignmentState {
  id: "default";
  nextGPSID: number;
  updatedAt?: Date;
}

export function parseAssignmentState(id: string, data: DocumentData): GPSAssignmentState | null {
  if (id !== "default" || typeof data.nextGPSID !== "number") return null;
  return { id: "default", nextGPSID: data.nextGPSID, updatedAt: dateValue(data.updatedAt) };
}

export function formatGPSID(value: number): string {
  return String(Math.max(0, Math.min(999, Math.trunc(value)))).padStart(3, "0");
}

export const GPS_ID_PATTERN = /^[0-9]{3}$/;

export interface GPSHistorySample extends GPSTrackerSample {
  id: string;
  userID: string;
  competitionInstanceID: string;
  gpsDocumentID: string;
  writtenAt?: Date;
}

export function historyDocumentID(gpstime: number): string {
  return `history_${gpstime}`;
}

export function parseHistorySample(id: string, data: DocumentData): GPSHistorySample | null {
  const sample = parseSample(data);
  const userID = stringValue(data.userID);
  const competitionInstanceID = stringValue(data.competitionInstanceID);
  const gpsDocumentID = stringValue(data.gpsDocumentID);
  if (!sample || !userID || !competitionInstanceID || !gpsDocumentID) return null;
  return {
    ...sample,
    id,
    userID,
    competitionInstanceID,
    gpsDocumentID,
    writtenAt: dateValue(data.writtenAt),
  };
}

// --- Live map derivations ported from GPSMapModel ---

export type GPSMapDeviceStatus = "active" | "stale" | "inactive";

export const DEVICE_STATUS_LABELS: Record<GPSMapDeviceStatus, string> = {
  active: "Active",
  stale: "Stale",
  inactive: "Inactive",
};

/**
 * Mirrors GPSMapModel.status(for:now:): window = max(90, 3× sample interval).
 * Takes only the three fields it reads so the public share map's leaner
 * device type can reuse it — status must decay with wall time on every client.
 */
export function trackerStatus(
  document: Pick<GPSTrackerDocument, "isTransmitting" | "lastUpdatedAt" | "sampleIntervalSeconds">,
  now: Date,
): GPSMapDeviceStatus {
  if (!document.isTransmitting) return "inactive";
  if (!document.lastUpdatedAt) return "stale";
  const activeWindowSeconds = Math.max(90, document.sampleIntervalSeconds * 3);
  return (now.getTime() - document.lastUpdatedAt.getTime()) / 1000 <= activeWindowSeconds ? "active" : "stale";
}

export function statusTintHex(status: GPSMapDeviceStatus, primaryColorHex: string, secondaryColorHex: string): string {
  switch (status) {
    case "active":
      return primaryColorHex;
    case "stale":
      return secondaryColorHex;
    case "inactive":
      return "6B7280";
  }
}

export interface GPSTrackedDeviceMarker {
  id: string;
  name: string;
  subtitle: string;
  latitude: number;
  longitude: number;
  tintHex: string;
  status: GPSMapDeviceStatus;
  photoURL?: string;
  initials: string;
  gpsID?: string;
  battery: number | null;
  speedMPH: number;
  heading?: number | null;
  selectedCourseName?: string;
  /** Known GPSAthleteCategory value or a free-form custom tracking identifier. */
  athleteCat?: string;
}

export function memberInitials(displayName: string): string {
  const parts = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? "");
  const result = parts.join("").toUpperCase();
  return result || "SB";
}

/** Mirrors GPSMapModel.deviceMarkers(from:members:...). */
export function deviceMarkers(
  documents: GPSTrackerDocument[],
  members: LeagueMemberSummary[],
  primaryColorHex: string,
  secondaryColorHex: string,
  now: Date,
): GPSTrackedDeviceMarker[] {
  const membersByUserID = new Map<string, LeagueMemberSummary>();
  for (const member of members) {
    if (!membersByUserID.has(member.userID)) {
      membersByUserID.set(member.userID, member);
    }
  }

  return documents.flatMap((document) => {
    const sample = document.latestSample;
    if (!sample) return [];
    if (Math.abs(sample.latitude) > 90 || Math.abs(sample.longitude) > 180 || (sample.latitude === 0 && sample.longitude === 0)) {
      return [];
    }

    const member = membersByUserID.get(document.userID);
    const status = trackerStatus(document, now);
    const courseName = document.selectedCourseKML?.name;
    const markerName = trimmedNonEmpty(document.displayName) ?? member?.displayName ?? document.userID.slice(0, 8);
    const gpsAlias = trimmedNonEmpty(document.gpsAlias);
    const aliasDetail = gpsAlias && gpsAlias.toLowerCase() !== markerName.toLowerCase() ? `GPS Alias: ${gpsAlias}` : undefined;
    const details = [
      aliasDetail,
      DEVICE_STATUS_LABELS[status],
      `${sample.speedUS_mph.toFixed(1)} mph`,
      sample.battery !== null ? `${sample.battery}% battery` : undefined,
      courseName,
    ].filter((entry): entry is string => !!entry);

    return [
      {
        id: document.id,
        name: markerName,
        subtitle: details.join(" • "),
        latitude: sample.latitude,
        longitude: sample.longitude,
        tintHex: statusTintHex(status, primaryColorHex, secondaryColorHex),
        status,
        photoURL: member?.photoURL,
        initials: member ? memberInitials(member.displayName) : document.userID.slice(0, 8).toUpperCase(),
        gpsID: document.gpsID,
        battery: sample.battery,
        speedMPH: sample.speedUS_mph,
        heading: sample.heading,
        selectedCourseName: courseName,
        athleteCat: document.athleteCat,
      },
    ];
  });
}

export interface GPSKMLSource {
  id: string;
  name: string;
  url: string;
}

/** Mirrors GPSMapModel.uniqueKMLSources — dedup selected course KMLs by URL. */
export function uniqueKMLSources(documents: GPSTrackerDocument[]): GPSKMLSource[] {
  const seen = new Set<string>();
  const sources: GPSKMLSource[] = [];
  for (const document of documents) {
    const kml = document.selectedCourseKML;
    if (!kml || seen.has(kml.url)) continue;
    seen.add(kml.url);
    sources.push({ id: `${kml.id}-${sbotSlug(kml.url)}`, name: kml.name, url: kml.url });
  }
  return sources;
}
